import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  Card,
  IssueCardInput,
  RequestPhysicalCardInput,
  ActivateCardInput,
  UpdateLimitsInput,
  UpdateCardSettingsInput,
  CardFilters,
  CardTransaction,
  TransactionFilters,
  Invoice,
  InvoiceFilters,
  PayInvoiceInput,
  InstallInvoiceInput,
  Dispute,
  CreateDisputeInput,
  CardIssuingConfig,
  UpdateCardConfigInput,
  CardStatistics,
} from '../types/cards';

// ==================== Cards ====================

export function useCards(filters?: CardFilters) {
  return useQuery({
    queryKey: ['cards', filters],
    queryFn: () =>
      api
        .get<{ cards: Card[]; total: number }>('/cards', { params: filters })
        .then((r) => r.data),
  });
}

export function useMyCards() {
  return useQuery({
    queryKey: ['my-cards'],
    queryFn: () => api.get<Card[]>('/cards/my').then((r) => r.data),
  });
}

export function useCard(cardId: string) {
  return useQuery({
    queryKey: ['card', cardId],
    queryFn: () => api.get<Card>(`/cards/${cardId}`).then((r) => r.data),
    enabled: !!cardId,
  });
}

export function useIssueCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IssueCardInput) =>
      api.post<Card>('/cards', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useRequestPhysicalCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RequestPhysicalCardInput) =>
      api.post<Card>('/cards/physical', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useActivateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: ActivateCardInput }) =>
      api.post<Card>(`/cards/${cardId}/activate`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useBlockCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, reason }: { cardId: string; reason: string }) =>
      api.post<Card>(`/cards/${cardId}/block`, { reason }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useUnblockCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) =>
      api.post<Card>(`/cards/${cardId}/unblock`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useCancelCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, reason }: { cardId: string; reason: string }) =>
      api.delete<Card>(`/cards/${cardId}`, { data: { reason } }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useUpdateCardLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: UpdateLimitsInput }) =>
      api.put<Card>(`/cards/${cardId}/limits`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

export function useUpdateCardSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: UpdateCardSettingsInput }) =>
      api.put<Card>(`/cards/${cardId}/settings`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['my-cards'] });
    },
  });
}

// ==================== Card Transactions ====================

export function useCardTransactions(cardId: string, filters?: TransactionFilters) {
  return useQuery({
    queryKey: ['card-transactions', cardId, filters],
    queryFn: () =>
      api
        .get<{ transactions: CardTransaction[]; total: number }>(
          `/cards/${cardId}/transactions`,
          { params: filters }
        )
        .then((r) => r.data),
    enabled: !!cardId,
  });
}

export function useCardTransaction(transactionId: string) {
  return useQuery({
    queryKey: ['card-transaction', transactionId],
    queryFn: () =>
      api.get<CardTransaction>(`/cards/transactions/${transactionId}`).then((r) => r.data),
    enabled: !!transactionId,
  });
}

export function useDisputeTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      data,
    }: {
      transactionId: string;
      data: CreateDisputeInput;
    }) =>
      api
        .post<Dispute>(`/cards/transactions/${transactionId}/dispute`, data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['card-transactions'] });
    },
  });
}

// ==================== Invoices ====================

export function useInvoices(cardId: string, filters?: InvoiceFilters) {
  return useQuery({
    queryKey: ['invoices', cardId, filters],
    queryFn: () =>
      api
        .get<{ invoices: Invoice[]; total: number }>(`/cards/${cardId}/invoices`, {
          params: filters,
        })
        .then((r) => r.data),
    enabled: !!cardId,
  });
}

export function useCurrentInvoice(cardId: string) {
  return useQuery({
    queryKey: ['current-invoice', cardId],
    queryFn: () =>
      api.get<Invoice>(`/cards/${cardId}/invoices/current`).then((r) => r.data),
    enabled: !!cardId,
  });
}

export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () =>
      api.get<Invoice>(`/cards/invoices/${invoiceId}`).then((r) => r.data),
    enabled: !!invoiceId,
  });
}

export function usePayInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, data }: { invoiceId: string; data?: PayInvoiceInput }) =>
      api.post<Invoice>(`/cards/invoices/${invoiceId}/pay`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['current-invoice'] });
    },
  });
}

export function useInstallInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, data }: { invoiceId: string; data: InstallInvoiceInput }) =>
      api
        .post(`/cards/invoices/${invoiceId}/installment`, data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['current-invoice'] });
    },
  });
}

// ==================== Config (Admin) ====================

export function useCardIssuingConfig() {
  return useQuery({
    queryKey: ['card-issuing-config'],
    queryFn: () =>
      api.get<CardIssuingConfig>('/cards/admin/config').then((r) => r.data),
  });
}

export function useUpdateCardIssuingConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCardConfigInput) =>
      api.put<CardIssuingConfig>('/cards/admin/config', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['card-issuing-config'] });
    },
  });
}

// ==================== Statistics (Admin) ====================

export function useCardStatistics() {
  return useQuery({
    queryKey: ['card-statistics'],
    queryFn: () =>
      api.get<CardStatistics>('/cards/admin/statistics').then((r) => r.data),
  });
}
